import React from 'react'
import { Form, Formik } from 'formik'
import get from 'lodash.get'
import { useMutation, useQueryClient } from 'react-query'
import { useParams } from 'react-router-dom'
import { Button, Card, Space, message } from 'antd'
import { formOptions, Payment as PaymentForm } from '../../../forms/blocks'
import { updateOrder } from '../../../network/Orders'
import { PRIORITY_MAP } from '../../../utils/utils'
import { useAuth } from '../../../hooks/useAuth'
import FormContent from '../FormContent'
import { getFromKey } from '../utils/orderHelpers'

const calculateTotal = (values) => {
  return (values.orderProducts || []).reduce((acc, prod) => {
    let price = 0
    if (prod.orderProductType === 'collection') {
      price = get(prod, 'orderCollection.price', 0)
    } else if (prod.orderProductType === 'adjustment') {
      price = get(prod, 'orderAdjustment.price', 0)
    } else if (prod.orderProductType === 'fixed_design') {
      price = get(prod, 'orderFixedDesign.price', 0)
    } else {
      price = get(prod, 'orderCustom.price', 0)
      const extraShirts = get(prod, 'orderCustom.orderCustomShirt.extraShirts', [])
      if (extraShirts && Array.isArray(extraShirts)) {
        extraShirts.forEach((item) => {
          price += get(item, 'priceNum', 0)
        })
      }
    }
    return acc + price
  }, 0)
}

const calculateBalance = (values) => {
  const total = calculateTotal(values)
  const discount = values.discount || 0
  const deposit1 = values.deposit || 0
  const deposit2 = values.deposit2 || 0
  return total - discount - deposit1 - deposit2
}

const EditOrderPayment = ({ data, orderId }) => {
  const formsPayment = [{ form: PaymentForm, keys: '' }]
  const { user } = useAuth()
  const { storeId } = useParams()
  const currentStore = user?.stores?.find((store) => store.store === storeId)
  const queryClient = useQueryClient()

  const editOrders = useMutation((val) => updateOrder(...val), {
    onSuccess: () => {
      message.success('order total updated !')
      queryClient.invalidateQueries(['getOrderDetails', { id: orderId }])
    },
  })

  return (
    <Formik
      initialValues={data}
      validate={(values) => {
        const errors = {}
        if (values.balance < 0) {
          errors.balance = 'Balance must be greater than 0'
        }
        return errors
      }}
      onSubmit={async (values) => {
        editOrders.mutate([values, orderId])
      }}
    >
      {({ values, errors, handleChange, ...rest }) => (
        <Form style={{ margin: '0px 14px' }}>
          <Card title="Payment details">
            <div
              style={{
                marginBottom: '16px',
                width: '50%',
                maxWidth: '50%',
                position: 'relative',
              }}
            >
              {(currentStore?.logo?.uri || !user?.franchiseStore) && (
                <img
                  src={
                    currentStore?.logo?.uri
                      ? `${process.env.REACT_APP_API_URL}/storage/store-logo/${currentStore.logo.uri}`
                      : '/images/atelier_logo.png'
                  }
                  alt="store logo"
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    display: 'block',
                    maxHeight: '150px',
                    objectFit: 'contain',
                  }}
                  loading="lazy"
                />
              )}
            </div>
            <table
              style={{
                width: '50%',
                borderCollapse: 'collapse',
                border: '1px solid #ddd',
              }}
            >
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px', border: '1px solid #ddd', backgroundColor: '#f2f2f2' }}>
                    Quantity
                  </th>
                  <th style={{ textAlign: 'left', padding: '8px', border: '1px solid #ddd', backgroundColor: '#f2f2f2' }}>
                    Product
                  </th>
                  <th style={{ textAlign: 'left', padding: '8px', border: '1px solid #ddd', backgroundColor: '#f2f2f2' }}>
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {values?.orderProducts
                  ?.map((prod, index) => {
                    const prodType = getFromKey(prod)
                    return { ...prod, priority: PRIORITY_MAP[prodType], originalIndex: index }
                  })
                  .sort((a, b) => b.priority - a.priority)
                  .flatMap((prod, sortedIndex) => {
                    const prodType = getFromKey(prod)
                    const originalIndex = prod.originalIndex
                    
                    if (prodType === 'adjustment') {
                      const quantity = get(prod, 'orderAdjustment.adjustmentPositions', []).length
                      return [
                        <tr
                          key={`${originalIndex}-adjustment`}
                          style={{
                            backgroundColor: sortedIndex % 2 === 0 ? '#fff' : '#f9f9f9',
                            borderBottom: '1px solid #ddd',
                          }}
                        >
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>{quantity}</td>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>Adjustment</td>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                            {data.currency ? data.currency.toUpperCase() : 'CNY'}{' '}
                            {get(prod, 'orderAdjustment.price', 0)}
                          </td>
                        </tr>
                      ]
                    }

                    // For shirt products with extra shirts, group by quality
                    if (prodType === 'shirt' && prod.orderProductType === 'custom') {
                      const mainQuality = get(prod, 'orderCustom.quality', '') || ''
                      const mainPrice = get(prod, 'orderCustom.price', 0)
                      const extraShirts = get(prod, 'orderCustom.orderCustomShirt.extraShirts', []) || []
                      
                      // Group shirts by quality
                      const qualityGroups = {}
                      
                      // Add main shirt to groups
                      const mainQualityKey = mainQuality || '(no quality)'
                      if (!qualityGroups[mainQualityKey]) {
                        qualityGroups[mainQualityKey] = { quantity: 0, price: 0 }
                      }
                      qualityGroups[mainQualityKey].quantity += 1
                      qualityGroups[mainQualityKey].price += mainPrice
                      
                      // Add extra shirts to groups
                      extraShirts.forEach((extraShirt) => {
                        const extraQuality = get(extraShirt, 'price', '') || ''
                        const extraPrice = get(extraShirt, 'priceNum', 0)
                        const extraQualityKey = extraQuality || '(no quality)'
                        
                        if (!qualityGroups[extraQualityKey]) {
                          qualityGroups[extraQualityKey] = { quantity: 0, price: 0 }
                        }
                        qualityGroups[extraQualityKey].quantity += 1
                        qualityGroups[extraQualityKey].price += extraPrice
                      })
                      
                      // If all shirts have the same quality, show one line
                      const qualityKeys = Object.keys(qualityGroups)
                      if (qualityKeys.length === 1) {
                        const singleQuality = qualityKeys[0]
                        const group = qualityGroups[singleQuality]
                        return [
                          <tr
                            key={`${originalIndex}-shirt`}
                            style={{
                              backgroundColor: sortedIndex % 2 === 0 ? '#fff' : '#f9f9f9',
                              borderBottom: '1px solid #ddd',
                            }}
                          >
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{group.quantity}</td>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                              {formOptions[prodType]?.name ?? 'Shirt'}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                              {data.currency ? data.currency.toUpperCase() : 'CNY'} {group.price.toFixed(2)}
                            </td>
                          </tr>
                        ]
                      }
                      
                      // If different qualities, show separate lines
                      return qualityKeys.map((quality, qualityIndex) => {
                        const group = qualityGroups[quality]
                        const displayQuality = quality === '(no quality)' ? '' : quality
                        return (
                          <tr
                            key={`${originalIndex}-shirt-${quality}-${qualityIndex}`}
                            style={{
                              backgroundColor: sortedIndex % 2 === 0 ? '#fff' : '#f9f9f9',
                              borderBottom: '1px solid #ddd',
                            }}
                          >
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{group.quantity}</td>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                              {formOptions[prodType]?.name ?? 'Shirt'}
                              {displayQuality ? ` (${displayQuality})` : ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                              {data.currency ? data.currency.toUpperCase() : 'CNY'} {group.price.toFixed(2)}
                            </td>
                          </tr>
                        )
                      })
                    }

                    // For non-shirt products or shirts without extra shirts, display normally
                    let price = 0
                    let quantity = 1
                    let total = 0

                    if (prodType === 'collection') {
                      price = get(prod, 'orderCollection.price', 0)
                    } else if (prodType === 'fixed_design') {
                      price = get(prod, 'orderFixedDesign.price', 0)
                    } else {
                      price = get(prod, 'orderCustom.price', 0)
                    }

                    if (prodType !== 'fixed_design' && prodType !== 'collection' && formOptions[prodType]) {
                      const extraShirts = get(prod, 'orderCustom.orderCustomShirt.extraShirts', [])
                      if (extraShirts) {
                        extraShirts.forEach((item) => {
                          price += get(item, 'priceNum', 0)
                        })
                        formOptions[prodType].forms.forEach((e) => {
                          total += get(prod, `${e.key}.extraShirts`, []).length
                        })
                      }
                      formOptions[prodType].forms.forEach((e) => {
                        total += get(prod, `${e.key}.addMore`, 0)
                      })
                      quantity = total + 1
                    }

                    return (
                      <tr
                        key={originalIndex}
                        style={{
                          backgroundColor: sortedIndex % 2 === 0 ? '#fff' : '#f9f9f9',
                          borderBottom: '1px solid #ddd',
                        }}
                      >
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>{quantity}</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                          {prodType === 'fixed_design'
                            ? `Fixed Design - ${get(prod, 'orderFixedDesign.productName.en', '') || get(prod, 'orderFixedDesign.productName.zh', '') || 'Product'}`
                            : prodType === 'collection'
                            ? `Fixed SKU - ${get(prod, 'orderCollection.productName', '') || 'Product'}`
                            : formOptions[prodType]
                            ? formOptions[prodType].name
                            : prodType}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                          {data.currency ? data.currency.toUpperCase() : 'CNY'} {price.toFixed(2)}
                        </td>
                      </tr>
                    )
                  })}
                <tr>
                  <td style={{ padding: '8px', fontWeight: 'bold', borderTop: '2px solid #000' }} />
                  <td style={{ padding: '8px', fontWeight: 'bold', borderTop: '2px solid #000' }}>Total:</td>
                  <td style={{ padding: '8px', fontWeight: 'bold', borderTop: '2px solid #000' }}>
                    {data.currency ? data.currency.toUpperCase() : 'CNY'} {calculateTotal(values)}
                  </td>
                </tr>
                <tr>
                  <td />
                  <td style={{ padding: '8px', fontWeight: 'bold' }}>Discount:</td>
                  <td style={{ padding: '8px' }}>
                    {data.currency ? data.currency.toUpperCase() : 'CNY'} {values.discount || 0}
                  </td>
                </tr>
                <tr>
                  <td />
                  <td style={{ padding: '8px', fontWeight: 'bold' }}>Deposit 1:</td>
                  <td style={{ padding: '8px' }}>
                    {data.currency ? data.currency.toUpperCase() : 'CNY'} {values.deposit || 0}
                  </td>
                </tr>
                <tr>
                  <td />
                  <td className="text-strong" style={{ padding: '8px' }}>Deposit 2:</td>
                  <td style={{ padding: '8px' }}>
                    {data.currency ? data.currency.toUpperCase() : 'CNY'} {values.deposit2 || 0}
                  </td>
                </tr>
                <tr>
                  <td />
                  <td className="text-strong" style={{ padding: '8px' }}>Balance:</td>
                  <td style={{ padding: '8px' }}>
                    {data.currency ? data.currency.toUpperCase() : 'CNY'} {calculateBalance(values)}
                  </td>
                </tr>
              </tbody>
            </table>
          </Card>
          <FormContent
            values={values}
            errors={errors}
            path=""
            handleChange={(e) => {
              handleChange(e)
              const discount = e.target.name === 'discount' ? e.target.value : values.discount || 0
              let deposit
              if (e.target.name === 'deposit') {
                deposit = e.target.value + get(values, 'deposit2', 0)
              } else if (e.target.name === 'deposit2') {
                deposit = get(values, 'deposit', 0) + e.target.value
              } else {
                deposit = get(values, 'deposit', 0) + get(values, 'deposit2', 0)
              }
              handleChange({
                target: {
                  name: 'balance',
                  value: values.totalSpent - discount - deposit,
                },
              })
            }}
            forms={formsPayment}
          />
          <Space style={{ margin: '14px 0px' }}>
            <Button
              loading={editOrders.isLoading}
              disabled={Object.keys(errors).length > 0}
              type="primary"
              htmlType="submit"
            >
              Save
            </Button>
            <Button disabled={editOrders.isLoading} onClick={() => rest.resetForm()} type="secondary">
              Cancel
            </Button>
          </Space>
        </Form>
      )}
    </Formik>
  )
}

export default EditOrderPayment
